// Owns the composer's attachment lifecycle: files are held locally until the
// user sends — they are NOT uploaded eagerly. Everything goes in one request
// when the message is sent.

import { useCallback, useEffect, useState } from 'react';
import type { IPendingAttachment } from '@interfaces/ICommsApi';

// The browser's side of the shared type: `blob` (undefined while a voice
// blob is still being fetched from the recorder) and `previewUrl`.
export type { IPendingAttachment };

export interface IVoiceRecording {
  url: string;
  mime: string;
  durationMs: number;
}

export interface IUseComposerAttachments {
  pending: IPendingAttachment[];
  hasPending: boolean;
  allReady: boolean; // false while any voice blob is still being fetched
  addFiles: (files: File[], kind: 'image' | 'file') => void;
  addVoice: (recording: IVoiceRecording) => void;
  remove: (localId: string) => void;
  clear: () => void;
}

function nextId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function voiceExtension(mime: string): string {
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mp4') || mime.includes('mpeg')) return 'mp4';
  return 'webm';
}

/** A finished recording, ready to send as is (no preview step). */
export function voiceAttachment(blob: Blob, durationMs: number): IPendingAttachment {
  return { localId: nextId('voice'), kind: 'voice', name: `voice.${voiceExtension(blob.type)}`, blob, durationMs };
}

function revokeAll(items: IPendingAttachment[]): void {
  items.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
}

export function useComposerAttachments(): IUseComposerAttachments {
  const [pending, setPending] = useState<IPendingAttachment[]>([]);

  const addFiles = useCallback((files: File[], kind: 'image' | 'file') => {
    for (const file of files) {
      const localId = nextId(kind);
      const previewUrl = kind === 'image' ? URL.createObjectURL(file) : undefined;
      setPending((prev) => [...prev, { localId, kind, name: file.name, blob: file, previewUrl }]);
    }
  }, []);

  const addVoice = useCallback((rec: IVoiceRecording) => {
    const localId = nextId('voice');
    const name = `voice.${voiceExtension(rec.mime)}`;
    // Add immediately with the recorder's blob URL for playback preview; fetch
    // the actual Blob in the background so it is ready when the user hits send.
    setPending((prev) => [
      ...prev,
      { localId, kind: 'voice', name, previewUrl: rec.url, durationMs: rec.durationMs },
    ]);
    fetch(rec.url)
      .then((r) => r.blob())
      .then((blob) => {
        setPending((prev) =>
          prev.map((p) => (p.localId === localId ? { ...p, blob } : p)),
        );
      })
      .catch(() => {
        // Blob fetch failed — remove the slot so the user isn't stuck
        setPending((prev) => {
          const item = prev.find((p) => p.localId === localId);
          if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
          return prev.filter((p) => p.localId !== localId);
        });
      });
  }, []);

  const remove = useCallback((localId: string) => {
    setPending((prev) => {
      const removed = prev.find((p) => p.localId === localId);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((p) => p.localId !== localId);
    });
  }, []);

  const clear = useCallback(() => {
    setPending((prev) => {
      revokeAll(prev);
      return [];
    });
  }, []);

  // Revoke any lingering preview URLs on unmount.
  useEffect(
    () => () => {
      setPending((prev) => {
        revokeAll(prev);
        return prev;
      });
    },
    [],
  );

  const hasPending = pending.length > 0;
  const allReady = pending.every((p) => p.blob != null);

  return { pending, hasPending, allReady, addFiles, addVoice, remove, clear };
}
